// Pure helpers for the stored sign-in (no Vue, no axios: runnable with node, see authSession.test.mjs).
//
// Why this exists (incident 2026-10-15, "invalid token" at sign-in that incognito does not show): the application keeps the last
// access token in localStorage and used to send it on EVERY request, including the ones that START a sign-in. After a few days
// that token is expired, and the backend answered 401 token_not_valid before it even looked at the request. The rules below make
// the stored token something that can only ever help, never block, a new sign-in.

// Endpoints that work without a session. The stored token is never sent to them: they ignore it, a stale one must not matter, and
// a token has no business in a request that is about getting a (new) token. Matched as path SUFFIXES so it works with or
// without a base URL and a query string.
export const PUBLIC_AUTH_PATHS = [
  "/api/v1/accounts/token/",
  "/api/v1/accounts/token/refresh/",
  "/api/v1/accounts/token/verify/",
  "/api/v1/accounts/otp/validate/",
  "/api/v1/accounts/otp/verify/",
  "/api/v1/accounts/otp/setup/",
  "/api/v1/accounts/azuread/authorize/",
  "/api/v1/accounts/azuread/callback/",
  "/api/v1/accounts/password/reset/email/",
  "/api/v1/accounts/password/reset/",
  "/api/v1/accounts/register/",
  "/api/v1/accounts/verify_resend/",
  "/api/version/",
  "/api/csrf/",
];
// "verify/<key>/" (e-mail confirmation link) has a variable tail.
const PUBLIC_AUTH_PREFIXES = ["/api/v1/accounts/verify/"];

function pathOf(url) {
  if (typeof url !== "string") return "";
  let p = url;
  const q = p.search(/[?#]/);
  if (q >= 0) p = p.slice(0, q);
  const scheme = p.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i);
  if (scheme) p = p.slice(scheme[0].length);
  if (p && !p.startsWith("/")) p = "/" + p;
  if (!p.endsWith("/")) p += "/";
  return p;
}

export function isPublicAuthUrl(url) {
  const p = pathOf(url);
  if (!p) return false;
  return PUBLIC_AUTH_PATHS.includes(p) || PUBLIC_AUTH_PREFIXES.some((x) => p.startsWith(x));
}

// What a leftover "token" can be in localStorage: a real JWT, or junk such as "undefined" / "null" / "" written by an earlier bug.
export function decodeJwtPayload(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? decodeURIComponent(
            atob(padded)
              .split("")
              .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
              .join("")
          )
        : Buffer.from(padded, "base64").toString("utf8");
    const obj = JSON.parse(json);
    return obj && typeof obj === "object" ? obj : null;
  } catch (e) {
    return null;
  }
}

export function isWellFormedToken(token) {
  return decodeJwtPayload(token) !== null;
}

// Expiry in ms since the epoch, or null when the token is junk or has no `exp`.
export function tokenExpiryMs(token) {
  const p = decodeJwtPayload(token);
  if (!p || typeof p.exp !== "number") return null;
  return p.exp * 1000;
}

// True when the token cannot be used any more (junk, no exp, or past exp). `skewMs` lets a caller treat a token that is about
// to expire as expired.
export function isTokenDead(token, now = Date.now(), skewMs = 0) {
  const exp = tokenExpiryMs(token);
  return exp === null || exp - skewMs <= now;
}

// The Authorization header value for a request, or null for "send none".
export function authHeaderFor(url, token) {
  if (isPublicAuthUrl(url)) return null;
  if (!isWellFormedToken(token)) return null;
  return `Bearer ${token}`;
}

// Everything the sign-in leaves in localStorage. Deliberately NOT the theme (`dark_theme`) (the old `jh_version` is removed at start-up since 2026-11-05).
export const SESSION_STORAGE_KEYS = [
  "token",
  "refreshToken",
  "jupyterToken",
  "user",
  "uuid",
  "otp_verified",
  "otp_validated",
  "profile",
  "isAdmin",
  "isSuperuser",
];

export function clearStoredSession(storage) {
  for (const k of SESSION_STORAGE_KEYS) {
    try {
      storage.removeItem(k);
    } catch (e) {
      // storage blocked: nothing is stored either
    }
  }
}

// Error codes of the backend (`code` in the JSON body of a 401) that mean "the stored sign-in is no good": drop it and sign in again.
export const DEAD_SESSION_CODES = ["token_not_valid", "2fa_verification_failed", "user_not_found", "user_inactive"];

export function isDeadSessionResponse(status, data) {
  if (status !== 401 || !data) return false;
  const code = typeof data.code === "string" ? data.code : data.code && data.code.code;
  return DEAD_SESSION_CODES.includes(code);
}

// The text for ?raven_error=<code> (set by the backend callback). `restart` = offer the "Sign in with CRSid again" button.
const RAVEN_MESSAGES = {
  not_configured: { text: "CRSid (Raven) sign-in is not available at the moment. Please use your username and password.", restart: false },
  no_linked_account: {
    text: "No CAMCEADHUB account is linked to that CRSid yet. Sign in with your username and password and add your CRSid on the Profile page, or ask an administrator.",
    restart: false,
  },
  not_approved: {
    text: "Your account is not active yet (it may still be waiting for approval). Please try again later or ask an administrator.",
    restart: false,
  },
  code_already_used: {
    text: "This sign-in link was already used or has expired (for example after a double click, the Back button, or a second tab). Nothing is wrong with your account: start again.",
    restart: true,
  },
  invalid_state: {
    text: "This sign-in took too long or its link has expired. Nothing is wrong with your account: start again.",
    restart: true,
  },
  missing_code_or_state: { text: "The sign-in was not completed. Start again.", restart: true },
  cancelled: { text: "The CRSid sign-in was cancelled. You can start again whenever you like.", restart: true },
  denied: { text: "Microsoft/Raven refused the sign-in. Try again, or use your username and password.", restart: true },
  service_unavailable: {
    text: "The Cambridge sign-in service could not be reached just now. Try again in a minute, or use your username and password.",
    restart: true,
  },
  token_exchange_failed: { text: "The Cambridge sign-in could not be completed. Start again.", restart: true },
  invalid_id_token: { text: "The Cambridge sign-in answer could not be verified. Start again; if it keeps happening tell an administrator.", restart: true },
  nonce_mismatch: { text: "This sign-in link does not belong to the sign-in you started. Start again.", restart: true },
  unknown_signing_key: { text: "The Cambridge sign-in answer could not be verified. Start again; if it keeps happening tell an administrator.", restart: true },
  no_crsid_claim: { text: "The Cambridge sign-in did not tell us your CRSid. Use your username and password, and tell an administrator.", restart: false },
};

export function ravenErrorInfo(code) {
  return (
    RAVEN_MESSAGES[code] || {
      text: "The CRSid sign-in did not work. Start again, or use your username and password.",
      restart: true,
    }
  );
}

// Reads access/refresh out of a URL fragment ("#access=..&refresh=.."); null when either is missing or not a JWT.
export function parseRavenFragment(hash) {
  const params = new URLSearchParams(String(hash || "").replace(/^#/, ""));
  const access = params.get("access");
  const refresh = params.get("refresh");
  if (!isWellFormedToken(access) || !isWellFormedToken(refresh)) return null;
  return { access, refresh };
}
